import React from "react";
import {
	Button,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	Modal,
	Row,
	Timestamp,
	TruncatedText,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { Student, StudentDataConsentDetailFragment, StudentNameAndImageFragment } from "../../../../Utilities";
import { ModelFormat, StudentDataConsentFormat } from "../../../Format";
import { StudentDataConsentStudentForm } from "../../../Forms";
import { StudentLink } from "../../../Links";
import { StudentIcon } from "../../Student/Icon";
import { StudentDataConsentUpdatePermissionRequired } from "../PermissionRequired";
import { StudentDataConsentSummary } from "../Summary";
import styles from "./styles.module.css";

export type StudentDataConsentDetailDisplayProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	studentDataConsent: StudentDataConsentDetailFragment;
};

export function StudentDataConsentDetailDisplay({ student, studentDataConsent }: StudentDataConsentDetailDisplayProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Column justify="spaced-start" verticalSpacing="0.875rem">
				<Row justify="space-between" align="center">
					<Row justify="spaced-start" align="center">
						<StudentIcon student={student} size={32} />
						<StudentLink instance={student} className="link--no-text-decoration" style={{ fontWeight: "bold" }} />
					</Row>

					<StudentDataConsentUpdatePermissionRequired>
						<Button variant="link" size="small" onClick={openModal}>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
								<Icon.PencilSimple size={14} /> Edit
							</Row>
						</Button>
					</StudentDataConsentUpdatePermissionRequired>
				</Row>

				<Column justify="spaced-start" verticalSpacing="1.5rem">
					<StudentDataConsentSummary studentDataConsent={studentDataConsent} />

					<span className={styles["last-updated"]}>
						<strong className={styles["last-updated__label"]}>Last Updated</strong>:{" "}
						<Timestamp value={studentDataConsent.updatedAt} className={styles["last-updated__value"]}>
							{StudentDataConsentFormat.Fields.updatedAt(studentDataConsent.updatedAt)}
						</Timestamp>
					</span>

					<strong>Verita is allowed to:</strong>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowPublishVisualMediaOfStudentGeneral}
						value={studentDataConsent.allowPublishVisualMediaOfStudentGeneral}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowPublishVisualMediaOfStudentForPromotion}
						value={studentDataConsent.allowPublishVisualMediaOfStudentForPromotion}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowPublishStudentWorkGeneral}
						value={studentDataConsent.allowPublishStudentWorkGeneral}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowPublishVisualMediaOfGuardianForPromotion}
						value={studentDataConsent.allowPublishVisualMediaOfGuardianForPromotion}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowShareInformationWithPearsonEdexcel}
						value={studentDataConsent.allowShareInformationWithPearsonEdexcel}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowShareInformationWithIbOrganization}
						value={studentDataConsent.allowShareInformationWithIbOrganization}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowShareInformationWithOnlinePlatforms}
						value={studentDataConsent.allowShareInformationWithOnlinePlatforms}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowBirthdayInCalendar}
						value={studentDataConsent.allowBirthdayInCalendar}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowSendNewsletters}
						value={studentDataConsent.allowSendNewsletters}
					/>

					<QuestionField
						label={StudentDataConsentFormat.fieldLabels.allowSendCalendarEvents}
						value={studentDataConsent.allowSendCalendarEvents}
					/>
				</Column>
			</Column>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} className={styles["modal"]}>
					<Modal.Header>
						<Heading.H2 noMargin>Data Consent</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<StudentDataConsentStudentForm.ControlledDetail
							studentDataConsent={studentDataConsent}
							formNameSuffix={student.id}
							onSuccess={closeModal}
							noChangesDialog
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type QuestionFieldProps = {
	label: string;
	value: boolean;
};

function QuestionField({ label, value }: QuestionFieldProps) {
	const { isExpanded, toggleIsExpanded, overflows, setOverflows } = TruncatedText.useTruncatedText();

	return (
		<Column justify="spaced-start">
			<Row justify="spaced-start" align="flex-start">
				<Conditional>
					<If condition={value}>
						<Icon.Check size={20} color="var(--theme-colors__green)" />
					</If>
					<Else>
						<Icon.X size={20} color="var(--theme-colors__pink)" />
					</Else>
				</Conditional>
				<Column justify="spaced-start">
					<TruncatedText
						lines={2}
						isExpanded={isExpanded}
						setOverflows={setOverflows}
						className={styles["question-field__label"]}
					>
						{label}
					</TruncatedText>

					<Row justify="space-between" align="center">
						<span
							className={
								styles["question-field__value"] + " " + styles[`question-field__value--${value ? "yes" : "no"}`]
							}
						>
							{ModelFormat.yesNoBoolean(value)}
						</span>

						<If condition={overflows}>
							<Button variant="link" size="small" onClick={toggleIsExpanded}>
								{isExpanded ? "Show less" : "Show more"}
							</Button>
						</If>
					</Row>
				</Column>
			</Row>
		</Column>
	);
}
